"use client";

import React, { useEffect } from "react";
import { Check, Loader, Send } from "lucide-react";
import { toast } from "sonner";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Toaster } from "@/components/ui/sonner";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  useEffect(() => {
    if (isSubmitted && !submitError) {
      toast.success("Inquiry Received", {
        description: successMessage
      });
    }
  }, [isSubmitted, submitError, successMessage]);
  if (isSubmitted && !submitError) {
    return <div data-section-id="120185" className="flex flex-col items-center justify-center py-16 text-center space-y-4 animate-in fade-in zoom-in-95 duration-500">
        <div className="size-16 rounded-full bg-secondary/10 border border-secondary/20 flex items-center justify-center text-secondary mb-2 shadow-[0_0_30px_rgba(var(--secondary),0.1)]">
          <Check className="size-8" />
        </div>
        <h3 className="text-2xl font-serif text-primary">Thank You</h3>
        <p className="text-muted-foreground max-w-sm mx-auto">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-8 border-border hover:bg-accent hover:text-accent-foreground transition-colors">
          Send Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary/80 font-medium">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="Putu Artha" className="bg-background/80 border-border/60 focus-visible:ring-primary/30 focus-visible:border-primary transition-all rounded-lg" {...field} />
              </FormControl>
              <FormDescription className="text-xs opacity-60">
                Full name for the booking
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary/80 font-medium">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="putu@example.com" className="bg-background/80 border-border/60 focus-visible:ring-primary/30 focus-visible:border-primary transition-all rounded-lg" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary/80 font-medium">
                WhatsApp Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+62 812 3456 7890" className="bg-background/80 border-border/60 focus-visible:ring-primary/30 focus-visible:border-primary transition-all rounded-lg" {...field} />
              </FormControl>
              <FormDescription className="text-xs opacity-60">
                Optional – for quick coordination
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary/80 font-medium">Event Type</FormLabel>
              <FormControl>
                <Input type="text" placeholder="Yoga, Pottery, or Corporate Workshop" className="bg-background/80 border-border/60 focus-visible:ring-primary/30 focus-visible:border-primary transition-all rounded-lg" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary/80 font-medium">Tell us about your vision</FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your layout needs, catering requirements, and estimated guest count..." className="min-h-[150px] resize-y bg-background/80 border-border/60 focus-visible:ring-primary/30 focus-visible:border-primary transition-all rounded-lg" {...field} />
            </FormControl>
            <FormDescription className="text-xs opacity-60">
              Include flexible dates or specific equipment needs
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-3">
          <div className="text-destructive mt-0.5"></div>
          <p className="text-sm text-destructive-foreground">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto min-w-[220px] h-12 text-base font-sans font-semibold tracking-wide bg-primary text-primary-foreground hover:bg-primary/90 shadow-md hover:shadow-lg transition-all rounded-lg">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending...
          </> : <>
            <Send className="mr-2 size-5" />
            Inquire for Hosting
          </>}
      </Button>
    </Form>;
}
export default function GetInTouch() {
  return <section id="host-your-workshop" className="bg-background text-foreground relative py-24 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_top_right,rgba(var(--secondary),0.05),transparent_50%)]" />
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_bottom_left,rgba(var(--primary),0.03),transparent_50%)]" />

      <Toaster position="bottom-right" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12 space-y-4">
            <h2 className="text-4xl md:text-5xl font-serif font-bold tracking-tight text-primary">
              Host Your Workshop
            </h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto leading-relaxed">
              Bring your community together in our Kilig Klub. We offer a flexible layout, 
              full projector setup, and customizable catering packages amidst the rice fields of Ubud.
            </p>
          </div>

          <div className="bg-card/40 backdrop-blur-md border border-border/40 rounded-2xl p-6 md:p-10 shadow-xl">
            <FormProvider formKey="host_workshop_form_workshop_space" formId="host_workshop_form_workshop_space" sectionName="Host Your Workshop" defaultValues={{
            name: "",
            email: "",
            phone: "",
            subject: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {},
            subject: {},
            message: {
              required: true
            }
          }} submitText="Inquire for Hosting" successMessage="Thank you for your interest! Our event coordinator will reach out to you shortly with availability and package details.">
              <ContactFormInner />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}